import { useState, useEffect } from "react";
import { X, Plus, Trash2, Save, ChevronLeft, ChevronRight, Dumbbell, Search, Link2, Unlink } from "lucide-react";
import ExercisePickerModal from "./ExercisePickerModal";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";
const SS_KLEUREN = { A:"#C9A84C", B:"#60a5fa", C:"#a78bfa", D:"#34d399", E:"#f87171", F:"#fb923c" };

function getNextLetter(oefeningen) {
  const used = new Set(oefeningen.map(o => o.superset_groep).filter(Boolean));
  for (const l of "ABCDEFGHIJKLMNOP") if (!used.has(l)) return l;
  return "A";
}

function buildGroepen(oefeningen) {
  const groups = [];
  let i = 0;
  while (i < oefeningen.length) {
    const groep = oefeningen[i].superset_groep;
    if (groep) {
      const items = [];
      while (i < oefeningen.length && oefeningen[i].superset_groep === groep) {
        items.push({ oef: oefeningen[i], idx: i });
        i++;
      }
      groups.push({ type: "superset", letter: groep, items });
    } else {
      groups.push({ type: "single", oef: oefeningen[i], idx: i });
      i++;
    }
  }
  return groups;
}

function OefeningRij({ oef, idx, onChange, onDelete, token, onStartSuperset, inSuperset }) {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div className="flex items-start gap-3 p-3.5 rounded-xl border bg-white/2 group"
      style={{ borderColor: inSuperset ? "transparent" : "rgba(255,255,255,0.08)" }}>
      {showPicker && (
        <ExercisePickerModal
          token={token}
          onClose={() => setShowPicker(false)}
          onSelect={ex => {
            onChange({ ...oef, naam: ex.naam, spiergroep: ex.spiergroep || oef.spiergroep });
            setShowPicker(false);
          }}
        />
      )}
      <div className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black text-black shrink-0 mt-0.5"
        style={{ background: GOLD }}>
        {idx + 1}
      </div>
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <input
            value={oef.naam || ""}
            onChange={e => onChange({ ...oef, naam: e.target.value })}
            placeholder="Oefening naam..."
            className="flex-1 bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-700 focus:outline-none focus:border-white/25"
          />
          <button onClick={() => setShowPicker(true)}
            data-testid={`open-sporter-exercise-picker-${idx}`}
            title="Verander oefening"
            className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors hover:bg-white/10 whitespace-nowrap"
            style={{ border: `1px solid ${GOLD}40`, color: GOLD }}>
            <Search size={11} /> Verander
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider block mb-1">Sets</label>
            <input type="number" min="1" value={oef.sets || 3}
              onChange={e => onChange({ ...oef, sets: parseInt(e.target.value) || 3 })}
              className="w-full bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm text-center focus:outline-none focus:border-white/25"
            />
          </div>
          <div>
            <label className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider block mb-1">Reps</label>
            <input value={oef.reps || "10"} onChange={e => onChange({ ...oef, reps: e.target.value })}
              placeholder="10"
              className="w-full bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm text-center focus:outline-none focus:border-white/25"
            />
          </div>
          <div>
            <label className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider block mb-1">Rust</label>
            <input value={oef.rust || ""} onChange={e => onChange({ ...oef, rust: e.target.value })}
              placeholder="90 sec"
              className="w-full bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm text-center focus:outline-none focus:border-white/25"
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1 mt-0.5">
        {!inSuperset && onStartSuperset && (
          <button onClick={onStartSuperset}
            data-testid={`start-superset-sporter-${idx}`}
            title="Superset starten"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-600 hover:text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-colors">
            <Link2 size={12} />
          </button>
        )}
        <button onClick={onDelete}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-700 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function SupersetBlok({ letter, items, token, onChangeOef, onDeleteOef, onAddToSuperset, onOntbinden }) {
  const kleur = SS_KLEUREN[letter] || GOLD;
  return (
    <div className="rounded-2xl border overflow-hidden"
      style={{ borderColor: `${kleur}35`, background: `${kleur}06` }}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b"
        style={{ borderColor: `${kleur}25`, background: `${kleur}10` }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black"
            style={{ background: kleur, color: "#000" }}>
            {letter}
          </div>
          <span className="text-xs font-black uppercase tracking-widest" style={{ color: kleur }}>
            Superset {letter}
          </span>
          <span className="text-zinc-600 text-xs">· {items.length} oefeningen</span>
        </div>
        <button onClick={onOntbinden} data-testid={`ontbinden-superset-sporter-${letter}`}
          className="flex items-center gap-1.5 text-zinc-600 hover:text-zinc-300 text-xs transition-colors px-2 py-1 rounded-lg hover:bg-white/5">
          <Unlink size={11} /> Ontbinden
        </button>
      </div>
      <div className="p-3 space-y-2">
        {items.map(({ oef, idx }) => (
          <OefeningRij key={oef.id || idx} oef={oef} idx={idx} token={token} inSuperset
            onChange={updated => onChangeOef(idx, updated)}
            onDelete={() => onDeleteOef(idx)}
          />
        ))}
        <button onClick={onAddToSuperset} data-testid={`add-to-superset-sporter-${letter}`}
          className="w-full py-2 rounded-xl border border-dashed text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
          style={{ borderColor: `${kleur}40`, color: kleur }}>
          <Plus size={12} /> Oefening toevoegen aan Superset {letter}
        </button>
      </div>
    </div>
  );
}

export default function SporterSchemaEditModal({ schemaId, sporter, token, onClose, onSaved }) {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actieveDag, setActieveDag] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!schemaId) return;
    setLoading(true);
    fetch(`${API}/api/admin/custom-schemas/${schemaId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setSchema(d); setLoading(false); })
      .catch(() => { setError("Schema laden mislukt"); setLoading(false); });
  }, [schemaId, token]);

  const updateOefening = (dagIdx, oefIdx, updated) => {
    const dagen = [...schema.dagen];
    const oefeningen = [...(dagen[dagIdx].oefeningen || [])];
    oefeningen[oefIdx] = updated;
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setSchema({ ...schema, dagen });
  };

  const deleteOefening = (dagIdx, oefIdx) => {
    const dagen = [...schema.dagen];
    let oefeningen = (dagen[dagIdx].oefeningen || []).filter((_, i) => i !== oefIdx);
    const groepTelling = {};
    oefeningen.forEach(o => { if (o.superset_groep) groepTelling[o.superset_groep] = (groepTelling[o.superset_groep]||0)+1; });
    oefeningen = oefeningen.map(o => ({
      ...o,
      superset_groep: o.superset_groep && groepTelling[o.superset_groep] < 2 ? null : o.superset_groep
    }));
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setSchema({ ...schema, dagen });
  };

  const addOefening = (dagIdx) => {
    const dagen = [...schema.dagen];
    const oefeningen = [...(dagen[dagIdx].oefeningen || [])];
    oefeningen.push({
      id: Math.random().toString(36).slice(2, 10),
      naam: "", spiergroep: "", sets: 3, reps: "10",
      gewicht: "", rust: "90 sec", tempo: "",
      notities: "", superset_groep: null, volgorde: oefeningen.length,
    });
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setSchema({ ...schema, dagen });
  };

  const startSuperset = (dagIdx, oefIdx) => {
    const dagen = [...schema.dagen];
    const oefeningen = [...(dagen[dagIdx].oefeningen || [])];
    const letter = getNextLetter(oefeningen);
    oefeningen[oefIdx] = { ...oefeningen[oefIdx], superset_groep: letter };
    oefeningen.splice(oefIdx + 1, 0, {
      id: Math.random().toString(36).slice(2, 10),
      naam: "", spiergroep: "", sets: oefeningen[oefIdx].sets || 3,
      reps: "10", gewicht: "", rust: "90 sec", tempo: "",
      notities: "", superset_groep: letter, volgorde: oefIdx + 1,
    });
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setSchema({ ...schema, dagen });
  };

  const addToSuperset = (dagIdx, letter) => {
    const dagen = [...schema.dagen];
    const oefeningen = [...(dagen[dagIdx].oefeningen || [])];
    let lastIdx = -1;
    oefeningen.forEach((o, i) => { if (o.superset_groep === letter) lastIdx = i; });
    oefeningen.splice(lastIdx + 1, 0, {
      id: Math.random().toString(36).slice(2, 10),
      naam: "", spiergroep: "", sets: 3, reps: "10",
      gewicht: "", rust: "90 sec", tempo: "",
      notities: "", superset_groep: letter, volgorde: lastIdx + 1,
    });
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setSchema({ ...schema, dagen });
  };

  const ontbindSuperset = (dagIdx, letter) => {
    const dagen = [...schema.dagen];
    const oefeningen = (dagen[dagIdx].oefeningen || []).map(o =>
      o.superset_groep === letter ? { ...o, superset_groep: null } : o
    );
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setSchema({ ...schema, dagen });
  };

  const saveSchema = async () => {
    setSaving(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/custom-schemas/${schemaId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          naam: schema.naam,
          beschrijving: schema.beschrijving || "",
          doel: schema.doel || "",
          niveau: schema.niveau || "",
          trainingsdagen: schema.trainingsdagen || 5,
          weken: schema.weken || 6,
          dagen: schema.dagen || [],
        }),
      });
      if (!r.ok) throw new Error("Opslaan mislukt");
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const dagCount = schema?.dagen?.length || 0;
  const huidigeDag = schema?.dagen?.[actieveDag];

  return (
    <div
      className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.80)", backdropFilter: "blur(8px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        data-testid="schema-edit-modal"
        className="w-full sm:w-[700px] max-h-[94vh] flex flex-col rounded-t-3xl sm:rounded-3xl border border-white/10"
        style={{ background: "#0d0d0d" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-white/8 shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Dumbbell size={14} style={{ color: GOLD }} />
              <h2 className="text-white font-black text-base leading-tight">
                {loading ? "Laden..." : schema?.naam}
              </h2>
            </div>
            <p className="text-zinc-600 text-xs">
              Schema van <span className="text-zinc-400">{sporter?.naam || sporter?.name}</span> · persoonlijk bewerken
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <X size={15} className="text-zinc-400" />
          </button>
        </div>

        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: GOLD, borderTopColor: "transparent" }} />
          </div>
        )}

        {!loading && schema && (
          <>
            {/* Schema naam */}
            <div className="px-6 pt-4 shrink-0">
              <label className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider block mb-1">Schema naam</label>
              <input
                value={schema.naam || ""}
                onChange={e => setSchema({ ...schema, naam: e.target.value })}
                className="w-full bg-transparent border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-white/25"
              />
            </div>

            {/* Dag-tabs */}
            <div className="flex items-center gap-1 px-6 py-3 overflow-x-auto shrink-0">
              {schema.dagen.map((dag, i) => (
                <button
                  key={i}
                  onClick={() => setActieveDag(i)}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all shrink-0"
                  style={actieveDag === i
                    ? { background: GOLD, color: "#000" }
                    : { background: "rgba(255,255,255,0.05)", color: "#71717a" }
                  }
                >
                  Dag {i + 1}
                </button>
              ))}
            </div>

            {/* Dag inhoud */}
            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-3">
              {huidigeDag && (
                <>
                  {/* Dag naam */}
                  <input
                    value={huidigeDag.dag_naam || ""}
                    onChange={e => {
                      const dagen = [...schema.dagen];
                      dagen[actieveDag] = { ...dagen[actieveDag], dag_naam: e.target.value };
                      setSchema({ ...schema, dagen });
                    }}
                    placeholder="Naam van deze trainingsdag..."
                    className="w-full bg-transparent border border-white/8 rounded-xl px-4 py-2.5 text-white font-bold text-sm focus:outline-none focus:border-white/20"
                  />

                  {/* Oefeningen — gegroepeerd */}
                  <div className="space-y-2">
                    {buildGroepen(huidigeDag.oefeningen || []).map((groep) =>
                      groep.type === "superset" ? (
                        <SupersetBlok
                          key={`ss-${groep.letter}`}
                          letter={groep.letter}
                          items={groep.items}
                          token={token}
                          onChangeOef={(oefIdx, updated) => updateOefening(actieveDag, oefIdx, updated)}
                          onDeleteOef={(oefIdx) => deleteOefening(actieveDag, oefIdx)}
                          onAddToSuperset={() => addToSuperset(actieveDag, groep.letter)}
                          onOntbinden={() => ontbindSuperset(actieveDag, groep.letter)}
                        />
                      ) : (
                        <OefeningRij
                          key={groep.oef.id || groep.idx}
                          oef={groep.oef}
                          idx={groep.idx}
                          token={token}
                          onStartSuperset={() => startSuperset(actieveDag, groep.idx)}
                          onChange={updated => updateOefening(actieveDag, groep.idx, updated)}
                          onDelete={() => deleteOefening(actieveDag, groep.idx)}
                        />
                      )
                    )}
                  </div>

                  {/* Oefening toevoegen */}
                  <button
                    onClick={() => addOefening(actieveDag)}
                    className="w-full py-2.5 rounded-xl border border-dashed border-white/15 text-zinc-600 hover:text-zinc-400 hover:border-white/25 text-sm flex items-center justify-center gap-2 transition-colors"
                  >
                    <Plus size={14} /> Oefening toevoegen
                  </button>
                </>
              )}
            </div>

            {/* Navigatie tussen dagen */}
            <div className="flex items-center justify-between px-6 pb-2 shrink-0">
              <button
                onClick={() => setActieveDag(d => Math.max(0, d - 1))}
                disabled={actieveDag === 0}
                className="flex items-center gap-1.5 text-zinc-600 hover:text-zinc-300 text-xs disabled:opacity-30 transition-colors"
              >
                <ChevronLeft size={14} /> Vorige dag
              </button>
              <span className="text-zinc-700 text-xs">Dag {actieveDag + 1} / {dagCount}</span>
              <button
                onClick={() => setActieveDag(d => Math.min(dagCount - 1, d + 1))}
                disabled={actieveDag === dagCount - 1}
                className="flex items-center gap-1.5 text-zinc-600 hover:text-zinc-300 text-xs disabled:opacity-30 transition-colors"
              >
                Volgende dag <ChevronRight size={14} />
              </button>
            </div>
          </>
        )}

        {/* Error */}
        {error && (
          <p className="text-red-400 text-xs text-center px-6 pb-2">{error}</p>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/8 flex gap-3 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-white/10 text-zinc-400 text-sm font-bold hover:bg-white/5 transition-colors"
          >
            Annuleren
          </button>
          <button
            data-testid="schema-edit-save-btn"
            onClick={saveSchema}
            disabled={saving || loading}
            className="flex-1 py-3 rounded-xl font-black text-sm text-black flex items-center justify-center gap-2 disabled:opacity-40 transition-all active:scale-[0.98]"
            style={{ background: GOLD }}
          >
            <Save size={14} />
            {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}
